import { AlmaAccountAnalytics } from "@almamedia/alma-account-analytics";
import { AlmaAccountCore, IdToken } from "@almamedia/alma-account-core";

import { getBusinessConfigByRealm } from "./utils";

export type AuthorizationHeaders = {
  Authorization?: string;
};

export type BusinessConnection = {
  audience: string;
  clientID: string;
  domain: string;
  realm: string;
  scope: string;
  redirectUri: string;
  returnTo: string;
};

export type UserGroup = "consumer" | "dealer" | "admin";

export enum LoginSource {
  LANDING_PAGE_CTA1 = "landingPageCtaButton-1",
  LANDING_PAGE_CTA2 = "landingPageCtaButton-2",
  FAQ_PAGE_CTA = "faqPageCtaButton-1",
  MY_AUCTIONS_PAGE_CTA = "myAuctionsPageCtaButton-1",
  HEADER_CTA = "headerCtaButton-1",
  FOOTER_CTA = "footerCtaButton-1",
  ARTICLE_PAGE_CTA = "articlePageCtaButton-1",
  WE_BUY_CARS_PAGE_CTA1 = "weBuyCarsPageCtaButton-1",
  WE_BUY_CARS_PAGE_CTA2 = "weBuyCarsPageCtaButton-2",
  WE_BUY_CARS_BY_MAKER_PAGE_CTA1 = "weBuyCarsByMakerPageCtaButton-1",
  WE_BUY_CARS_BY_MAKER_PAGE_CTA2 = "weBuyCarsByMakerPageCtaButton-2",
  STEP_REGNO_AND_MILEAGE = "auctionWizardSaveDraftButton-step_regno_and_mileage",
  STEP_BASIC_VEHICLE_INFO = "auctionWizardSaveDraftButton-step_basic_vehicle_info",
  STEP_BASIC_INFO = "auctionWizardSaveDraftButton-step_basic_info",
  STEP_VEHICLE_CONDITION = "auctionWizardSaveDraftButton-step_vehicle_condition",
  STEP_DAMAGES = "auctionWizardSaveDraftButton-step_damages",
  STEP_ACCESSORIES = "auctionWizardSaveDraftButton-step_accessories",
  STEP_MEDIA = "auctionWizardSaveDraftButton-step_media",
  STEP_FREE_DESCRIPTION = "auctionWizardSaveDraftButton-step_free_description",
  MOTO_STEP_FREE_DESCRIPTION = "auctionWizardSaveDraftButton-moto_step_free_description",
  CONSUMER_HEADER_LOGIN = "consumerHeaderLoginButton",
  CONSUMER_HEADER_SIGNUP = "consumerHeaderSignupButton",
  CONSUMER_MENU = "consumerMenuLoginButton",
  DEALER_HEADER_LOGIN = "dealerHeaderLoginButton",
  DEALER_HEADER_SIGNUP = "dealerHeaderSignupButton",
  DEALER_HOME_PAGE_LOGIN = "dealerHomePageLoginButton",
  DEALER_SSO_LOGIN = "dealerSsoLogin",
  DEALER_HOME_PAGE_SIGNUP = "dealerHomePageSignupButton",
  ADMIN_HEADER = "adminHeaderLoginButton",
  ADMIN_MENU = "adminMenuLoginButton",
  ADMIN_HOME_PAGE_LOGIN = "adminHomePageLoginButton",
  AUCTION_WIZARD_SAVE_DRAFT = "auctionWizardSaveDraftButton",
  VERTICAL_PREFILL = "verticalPrefill"
}

const almaSsoConsumerConfiguration = {
  // @ts-ignore
  audience: import.meta.env.VITE_ALMA_SSO_AUDIENCE, // @ts-ignore
  clientID: import.meta.env.VITE_ALMA_SSO_CLIENT_ID, // @ts-ignore
  domain: import.meta.env.VITE_ALMA_SSO_DOMAIN,
  realm: "Almatunnus",
  scope: "openid profile email", // @ts-ignore
  redirectUri: import.meta.env.VITE_ALMA_SSO_REDIRECT_URL, // @ts-ignore // DO WE STILL NEED THIS?
  returnTo: import.meta.env.VITE_ALMA_SSO_RETURN_TO // @ts-ignore
};

const almaSsoDealerConfiguration = {
  // @ts-ignore
  audience: import.meta.env.VITE_ALMA_SSO_AUDIENCE, // @ts-ignore
  clientID: import.meta.env.VITE_ALMA_SSO_CLIENT_ID_DEALER, // @ts-ignore
  domain: import.meta.env.VITE_ALMA_SSO_DOMAIN,
  realm: "Almatunnus-Business",
  scope: "openid profile email", // @ts-ignore
  redirectUri: import.meta.env.VITE_ALMA_SSO_REDIRECT_URL, // @ts-ignore // DO WE STILL NEED THIS?
  returnTo: import.meta.env.VITE_ALMA_SSO_RETURN_TO // @ts-ignore
};

// Add helper to store the connection in localStorage
const storeConnection = (connection: string | null) => {
  if (connection) {
    localStorage.setItem("AUTH_CONNECTION", connection);
  } else {
    localStorage.removeItem("AUTH_CONNECTION");
  }
};

// Add helper to get stored connection
const getStoredConnection = (): string | null => {
  return localStorage.getItem("AUTH_CONNECTION");
};

const getConfig = (userGroup: UserGroup, connection?: string | null) => {
  // First check URL connection parameter, then fallback to stored connection
  const effectiveConnection = connection || getStoredConnection();

  if (userGroup === "dealer" && effectiveConnection) {
    const businessConfig = getBusinessConfigByRealm(effectiveConnection);
    if (businessConfig) {
      // Store the connection for subsequent auth operations
      storeConnection(effectiveConnection);
      return businessConfig;
    }
  }

  // Clear stored connection if not using a business configuration
  storeConnection(null);
  return userGroup === "dealer"
    ? almaSsoDealerConfiguration
    : almaSsoConsumerConfiguration;
};

let initializedUserGroup: "consumer" | "dealer" | "admin" | undefined =
  undefined;

let analytics: AlmaAccountAnalytics | undefined = undefined;

//
// HELPERS
//

const hasUnixTimeExpired = (unixTime: number): boolean => {
  const currentTime = Math.floor(Date.now() / 1000);
  return currentTime + 900 > unixTime;
  // return currentTime > unixTime;
};

const isIdTokenExpired = (idToken: IdToken | undefined | string): boolean => {
  if (
    idToken === undefined ||
    typeof idToken === "string" ||
    idToken.exp === undefined
  ) {
    return true;
  }
  return hasUnixTimeExpired(idToken.exp);
};

const homePagePath = (userGroup: UserGroup) => {
  const origin = window.location.origin;
  switch (userGroup) {
    case "admin":
      return `${origin}/admin`;
    case "dealer":
      return `${origin}/dealer`;
    default:
      return origin;
  }
};

/** OAuth error codes from Auth0 silent auth that are normal when no SSO session exists */
const KNOWN_SILENT_AUTH_ERROR_CODES = new Set([
  "login_required",
  "consent_required",
  "interaction_required",
  "missing_refresh_token"
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const oauthErrorCodeFrom = (error: unknown): string | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }
  const direct = error.error;
  if (typeof direct === "string") {
    return direct;
  }
  const cause = error.cause;
  if (isRecord(cause) && typeof cause.error === "string") {
    return cause.error;
  }
  return undefined;
};

const isSilentAuthError = (error: unknown): boolean => {
  const code = oauthErrorCodeFrom(error);
  return code !== undefined && KNOWN_SILENT_AUTH_ERROR_CODES.has(code);
};

//
// This module follows the Immediately Invoked Function Expression (IIFE) pattern
//
export const authModule = (() => {
  //
  // EXPOSED FUNCTIONS
  //
  const initAuth = async (
    userGroup: UserGroup,
    connection?: string | null
  ): Promise<void> => {
    if (initializedUserGroup === userGroup) {
      return;
    }

    const core = AlmaAccountCore.getInstance();
    const config = getConfig(userGroup, connection);

    try {
      if (initializedUserGroup === undefined) {
        // Initialize with connection-specific config if present
        // NOTE: disableAutologin is set to true to disable auto-login functionality.
        // Set to false to re-enable auto-login in the future.
        await core.init({
          ...config,
          realm: connection || config.realm, // Use connection as realm if present
          disableAutologin: true
        });
        initializedUserGroup = userGroup;
        analytics = AlmaAccountAnalytics.getInstance({
          clientId: config.clientID
        });
      }
    } catch (error) {
      throw new Error("Failed to initialize authentication!");
    }
  };

  const performLogout = async () => {
    const returnUrl = homePagePath(initializedUserGroup ?? "consumer");
    try {
      storeConnection(null);
      const core = AlmaAccountCore.getInstance();
      const isAuthenticated = await core.isAuthenticated();
      if (isAuthenticated) {
        return core.logout(returnUrl);
      }
    } catch (error) {
      console.error("performLogout failed!", error);
      return Promise.reject(new Error("performLogout failed!"));
    }
  };

  // the caller makes sure that AlmaAccountCore is initialized
  // note: the id token may be expired
  const getIdToken = async () => {
    const core = AlmaAccountCore.getInstance();
    if (!(await core.isAuthenticated())) {
      throw new Error("Not authenticated!");
    }
    return core.getIdToken(true); // raw = true, get the raw token
  };

  const hasValidIdToken = async () => {
    const core = AlmaAccountCore.getInstance();
    const hasToken = await core.isAuthenticated();
    if (hasToken) {
      const token = await core.getIdToken(false);
      const isValid = !isIdTokenExpired(token);
      return isValid;
    } else {
      return false;
    }
  };

  // refresh all tokens (we mainly use this to check the user's email verification status)
  // IMPORTANT: do not hammer this function! Alma Account has global (!) rate limits in place
  const refreshUserInfo = async () => {
    if (!(await hasValidIdToken())) {
      throw new Error("No valid id token!"); // temporary solution until we implement refresh tokens
    }
    const core = AlmaAccountCore.getInstance();
    await core.silentAuthenticationWithPopup({ cacheMode: "off" });
  };

  /**
   * Attempts silent authentication using Auth0 SSO session checking.
   * This function checks for an existing SSO session across Alma Media services
   * and authenticates the user silently without user interaction.
   *
   * @param userGroup - The user group to attempt autologin for (typically "consumer")
   * @returns Promise resolving to ID token string if autologin succeeds, undefined otherwise
   */
  const attemptSilentLogin = async (
    userGroup: UserGroup
  ): Promise<string | undefined> => {
    const core = AlmaAccountCore.getInstance();

    try {
      // Initialize if not already initialized
      if (initializedUserGroup !== userGroup) {
        await initAuth(userGroup);
      }

      // Attempt silent authentication
      await core.autologin();

      // Verify authentication was successful
      const isAuthenticated = await core.isAuthenticated();
      if (!isAuthenticated) {
        return undefined;
      }

      // Get the ID token for API calls
      const tokenResult = await core.getIdToken(true); // raw = true, get the raw token
      // Ensure we return a string (raw token) or undefined
      const token = typeof tokenResult === "string" ? tokenResult : undefined;
      if (!token) {
        return undefined;
      }
      return token;
    } catch (error) {
      if (isSilentAuthError(error)) {
        return undefined;
      }
      console.error("[attemptSilentLogin] Unexpected error", error);
      throw error;
    }
  };

  const sendLoginEvent = async () => {
    try {
      const core = AlmaAccountCore.getInstance();
      const user = await core.getUserInfo();
      if (analytics && user) {
        await analytics.sendUserEvent({ user });
      }
    } catch (e) {
      console.warn("analytics sendLoginEvent failed", e);
    }
  };

  const performRedirectLogin = async (
    userGroup: UserGroup,
    source: LoginSource,
    connection?: string | null,
    returnUrl?: string
  ): Promise<void> => {
    if (initializedUserGroup !== userGroup) {
      await initAuth(userGroup, connection);
    }
    // Store userGroup so AuthInitializationProvider can call initAuth() on the
    // callback page before handleAuthentication() — the in-memory singleton is
    // wiped on redirect and must be re-initialized before exchanging the auth code.
    localStorage.setItem("PREVIOUS_SESSION", userGroup);
    const core = AlmaAccountCore.getInstance();
    await core.login({
      authorizationParams: {
        theme: "light",
        lang: "fi",
        source,
        screen_hint: "login",
        ...(connection && { connection })
      },
      appState: { returnUrl: returnUrl ?? window.location.href, userGroup }
    });
  };

  const performRedirectSignup = async (
    userGroup: UserGroup,
    source: LoginSource
  ): Promise<void> => {
    if (initializedUserGroup !== userGroup) {
      await initAuth(userGroup);
    }
    localStorage.setItem("PREVIOUS_SESSION", userGroup);
    const core = AlmaAccountCore.getInstance();
    await core.login({
      authorizationParams: {
        theme: "light",
        lang: "fi",
        source,
        screen_hint: "signup"
      },
      appState: { returnUrl: window.location.href, userGroup }
    });
  };

  const handleAuthentication = async (): Promise<
    { appState?: Record<string, unknown> } | undefined
  > => {
    const core = AlmaAccountCore.getInstance();
    return core.handleAuthentication() as Promise<
      { appState?: Record<string, unknown> } | undefined
    >;
  };

  const sendLogoutEvent = async () => {
    try {
      const core = AlmaAccountCore.getInstance();
      const user = await core.getUserInfo();
      if (analytics && user) {
        await analytics.sendLogout({
          almaIDHash: user?.["https://almamedia.net/almaIDHash"],
          connection: user?.["https://almamedia.net/connection"],
          lang: user?.["https://almamedia.net/lang"]
        });
      }
    } catch (e) {
      console.warn("analytics sendLogoutEvent failed", e);
    }
  };

  return {
    initAuth,
    hasValidIdToken,
    refreshUserInfo,
    performRedirectLogin,
    performRedirectSignup,
    handleAuthentication,
    performLogout,
    getIdToken,
    sendLoginEvent,
    sendLogoutEvent,
    attemptSilentLogin
  };
})();

Object.freeze(authModule);
